import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  LinearProgress,
  TableSortLabel,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import { themeColors } from '../styles/theme';
import { getCoverageStatistics } from '../API';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface CityStats {
  city: string;
  verified_gardens: number;
}

const sortData = (data: CityStats[], sortBy: string, sortDirection: 'asc' | 'desc'): CityStats[] => {
  return [...data].sort((a, b) => {
    if (sortBy === 'city') {
      return sortDirection === 'asc' 
        ? a.city.localeCompare(b.city)
        : b.city.localeCompare(a.city);
    } else if (sortBy === 'verified_gardens') {
      return sortDirection === 'asc' 
        ? a.verified_gardens - b.verified_gardens
        : b.verified_gardens - a.verified_gardens;
    }
    return 0;
  });
}

const CoverageStats = () => {
  const [stats, setStats] = useState<CityStats[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('verified_gardens');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await getCoverageStatistics();
        if (response.data && response.data.coverage_stats) {
          setStats(response.data.coverage_stats);
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch coverage statistics:', err);
        setError('Failed to load coverage statistics');
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Prevent tooltip click/touch from triggering column sort
  const handleTooltipClick = (event: React.MouseEvent | React.TouchEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  // Filter stats based on search term
  const filteredStats = stats.filter(row => 
    row.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTotalStats = (data: CityStats[]) => {
    const totalVerified = data.reduce((sum, city) => sum + city.verified_gardens, 0);    
    return { totalVerified };
  };

  const handleCityClick = (city: string) => {
    const encodedCity = encodeURIComponent(city);
    navigate(`/sunny-pubs-in/${encodedCity}`);
  };

  // Add a helper function to safely encode city names for SEO-friendly URLs
  const safelyEncodeCity = (city: string): string => {
    if (!city) return '';
    return encodeURIComponent(city);
  };

  const sortedStats = sortData(filteredStats, sortBy, sortDirection);
  const { totalVerified } = getTotalStats(filteredStats);

  return (
    <Box sx={{ margin: '20px 0 40px' }}>
      <Typography 
        variant="h5" 
        sx={{ 
          marginBottom: '16px',
          fontFamily: "'DM-Sans', sans-serif",
          color: themeColors.darkBackground,
          textAlign: 'center'
        }}
      >
        Areas with Verified Gardens
      </Typography>

      {loading ? (
        <Box sx={{ width: '100%', padding: '20px 0' }}>
          <LinearProgress sx={{ 
            backgroundColor: `${themeColors.lightYellow}40`,
            '& .MuiLinearProgress-bar': {
              backgroundColor: themeColors.orange
            }
          }} />
          <Typography variant="body2" sx={{ textAlign: 'center', marginTop: '8px' }}>
            Loading statistics...
          </Typography>
        </Box>
      ) : error ? (
        <Typography 
          variant="body1" 
          color="error"
          sx={{ textAlign: 'center', padding: '20px' }}
        >
          {error}
        </Typography>
      ) : (
        <>
          {/* Search Bar */}
          <Box 
            sx={{ 
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <TextField
              placeholder="Search area..."
              value={searchTerm}
              onChange={handleSearch}
              variant="outlined"
              size="small"
              sx={{
                width: { xs: '100%', sm: '60%', md: '50%' },
                backgroundColor: 'white',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: themeColors.orange,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: themeColors.orange,
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={handleClearSearch}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Stats Summary */}
          {searchTerm && (
            <Typography
              variant="body2"
              sx={{ 
                textAlign: 'center', 
                marginBottom: '8px',
                fontStyle: 'italic'
              }}
            >
              Found {sortedStats.length} areas matching "{searchTerm}"
            </Typography>
          )}

          {/* Table Container with Fixed Height and Scrolling */}
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              borderRadius: '12px',
              height: { xs: '400px', sm: '500px' }, // Fixed height for scrolling
              overflow: 'auto',               // Enable scrolling
              background: `linear-gradient(to bottom, ${themeColors.lightYellow}10, transparent)`,
              '& .MuiTableCell-root': {
                padding: isMobile ? '10px 8px' : '16px',
                fontSize: isMobile ? '0.8rem' : '0.9rem',
              },
              // Better scrollbar styling
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: `${themeColors.orange}80`,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: themeColors.orange,
                },
              },
            }}
          >
            <Table stickyHeader aria-label="coverage statistics table" size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      background: 'rgba(255, 247, 213, 0.85)',
                      backdropFilter: 'blur(10px)',
                      borderBottom: `2px solid ${themeColors.orange}`,
                      color: themeColors.darkBackground,
                      padding: '16px 8px 16px 16px',
                      width: '60%'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <TableSortLabel
                        active={sortBy === 'city'}
                        direction={sortBy === 'city' ? sortDirection : 'asc'}
                        onClick={() => handleSort('city')}
                      >
                        Area
                      </TableSortLabel>
                      <Tooltip 
                        title="E.g. a town or a city" 
                        placement="top"
                        enterTouchDelay={0}
                        leaveTouchDelay={2500}
                      >
                        <IconButton 
                          size="small" 
                          sx={{ 
                            ml: 0.5, 
                            p: 0,
                            color: `${themeColors.darkBackground}99`,
                            '&:hover': { backgroundColor: 'transparent' }
                          }}
                          onClick={handleTooltipClick}
                          onTouchStart={handleTooltipClick}
                          disableTouchRipple
                          aria-label="Area information"
                        >
                          <HelpOutlineIcon fontSize="small" sx={{ 
                            fontSize: isMobile ? '1rem' : '0.9rem',
                            color: isMobile ? themeColors.orange : `${themeColors.darkBackground}99`,
                          }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontWeight: 'bold', 
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      background: 'rgba(255, 247, 213, 0.85)',
                      backdropFilter: 'blur(10px)',
                      borderBottom: `2px solid ${themeColors.orange}`,
                      color: themeColors.darkBackground,
                      padding: '16px 16px 16px 8px',
                      width: '40%'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <TableSortLabel
                        active={sortBy === 'verified_gardens'}
                        direction={sortBy === 'verified_gardens' ? sortDirection : 'asc'}
                        onClick={() => handleSort('verified_gardens')}
                      >
                        Verified Gardens
                      </TableSortLabel>
                      <Tooltip 
                        title="This is the number of gardens that have been verified in this area." 
                        placement="top"
                        enterTouchDelay={0}
                        leaveTouchDelay={2500}
                      >
                        <IconButton 
                          size="small" 
                          sx={{ 
                            ml: 0.5, 
                            p: 0,
                            color: `${themeColors.darkBackground}99`,
                            '&:hover': { backgroundColor: 'transparent' }
                          }}
                          onClick={handleTooltipClick}
                        >
                          <HelpOutlineIcon fontSize="small" sx={{ 
                            fontSize: isMobile ? '1rem' : '0.9rem',
                            color: isMobile ? themeColors.orange : `${themeColors.darkBackground}99`,
                          }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedStats.length > 0 ? (
                  sortedStats.map((row) => {                    
                    return (
                      <TableRow
                        key={row.city}
                        onClick={() => row.city !== 'Unknown' ? handleCityClick(row.city) : null}
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-of-type(odd)': { backgroundColor: `${themeColors.lightYellow}20` },
                          '&:hover': { backgroundColor: `${themeColors.lightYellow}40` },
                          cursor: row.city !== 'Unknown' ? 'pointer' : 'default',
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row"
                          sx={{ 
                            fontWeight: row.city === 'Unknown' ? 'normal' : 500,
                            fontStyle: row.city === 'Unknown' ? 'italic' : 'normal'
                          }}
                        >
                          {row.city}
                        </TableCell>
                        <TableCell align="right">
                          {row.verified_gardens}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        No areas found matching your search
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow
                  sx={{ 
                    backgroundColor: themeColors.lightYellow,
                    '& td': { 
                      fontWeight: 'bold',
                      borderTop: `2px solid ${themeColors.orange}`,
                      color: themeColors.darkBackground
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    {searchTerm ? 'Filtered Total' : 'Total'}
                  </TableCell>
                  <TableCell align="right">
                    {totalVerified}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '16px',
            padding: '0 8px'
          }}>
            <Typography
              variant="body2"
              sx={{
                textAlign: isMobile ? 'center' : 'left',
                fontStyle: 'italic',
                color: 'rgba(0,0,0,0.6)',
                marginBottom: isMobile ? '8px' : 0
              }}
            >
              Some venues may have multiple gardens. Help us improve coverage!
            </Typography>
            
            <Typography
              variant="body2"
              sx={{
                textAlign: isMobile ? 'center' : 'right',
                fontSize: '0.8rem',
                color: '#666'
              }}
            >
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CoverageStats;